function clamp(value) {
  return Math.max(0, Math.min(1, value));
}

function normalizeText(value = '') {
  return String(value)
    .toLocaleLowerCase()
    .normalize('NFKD')
    .replace(/[\u064B-\u065F\u0670]/g, '')
    .replace(/[أإآ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .replace(/[ؤئ]/g, 'ء')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function levenshtein(left, right) {
  if (!left) return right.length;
  if (!right) return left.length;
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let i = 1; i <= left.length; i += 1) {
    const current = [i];
    for (let j = 1; j <= right.length; j += 1) {
      current[j] = Math.min(
        current[j - 1] + 1,
        previous[j] + 1,
        previous[j - 1] + (left[i - 1] === right[j - 1] ? 0 : 1),
      );
    }
    previous.splice(0, previous.length, ...current);
  }
  return previous[right.length];
}

function tokenScore(query, candidate) {
  if (!query || !candidate) return 0;
  if (query === candidate) return 1;
  if (query.includes(candidate) || candidate.includes(query)) {
    return 0.78 + Math.min(query.length, candidate.length) / Math.max(query.length, candidate.length) * 0.22;
  }
  return Math.max(0, 1 - levenshtein(query, candidate) / Math.max(query.length, candidate.length));
}

export function scoreRestaurantText(input, restaurant) {
  const query = normalizeText(input);
  if (!query) return 0;
  const candidates = [restaurant.name, restaurant.nameAr, ...(restaurant.aliases || [])]
    .map(normalizeText)
    .filter(Boolean);
  const queryWords = query.split(' ').filter((word) => word.length > 1);
  let best = 0;

  for (const candidate of candidates) {
    best = Math.max(best, tokenScore(query, candidate));
    const candidateWords = candidate.split(' ').filter((word) => word.length > 1);
    if (queryWords.length && candidateWords.length) {
      const matched = queryWords.filter((word) =>
        candidateWords.some((candidateWord) => tokenScore(word, candidateWord) >= 0.72),
      ).length;
      best = Math.max(best, matched / Math.max(queryWords.length, candidateWords.length));
    }
  }
  return clamp(best);
}

function normalizeVisualEvidence(value) {
  if (typeof value === 'number') {
    return {
      best: value,
      second: 0,
      meanTop: value,
      consensus: value,
      supportCount: value >= 0.68 ? 1 : 0,
      referenceCount: value > 0 ? 1 : 0,
      topScores: value > 0 ? [value] : [],
    };
  }

  return {
    best: Number(value?.best || 0),
    second: Number(value?.second || 0),
    meanTop: Number(value?.meanTop || 0),
    consensus: Number(value?.consensus || 0),
    supportCount: Number(value?.supportCount || 0),
    referenceCount: Number(value?.referenceCount || 0),
    topScores: Array.isArray(value?.topScores) ? value.topScores : [],
  };
}

function calibrateVisualScore(rawScore) {
  // Generic, unrelated photos often produce a moderate raw fingerprint score.
  // Scores below 0.55 therefore represent no useful recognition evidence.
  return clamp((rawScore - 0.55) / 0.33);
}

function buildVisualScore(evidence) {
  const best = calibrateVisualScore(evidence.best);
  const consensus = calibrateVisualScore(evidence.consensus);
  let score = best * 0.74 + consensus * 0.26;
  if (evidence.supportCount >= 2) score += 0.04;
  return clamp(score);
}

function acceptsScannedMatch(candidate) {
  const { textScore, visualScore, visualEvidence, confidence } = candidate;
  const strongText = textScore >= 0.79;
  const strongCombined = textScore >= 0.58
    && visualEvidence.best >= 0.60
    && confidence >= 0.57;
  const supportingCombined = textScore >= 0.47
    && visualEvidence.best >= 0.69
    && confidence >= 0.55;

  const veryStrongSingleReference = visualEvidence.best >= 0.88
    && visualScore >= 0.78;
  const strongMultipleReferences = visualEvidence.referenceCount >= 2
    && visualEvidence.best >= 0.78
    && visualEvidence.second >= 0.67
    && visualEvidence.supportCount >= 2
    && visualScore >= 0.62;

  return strongText
    || strongCombined
    || supportingCombined
    || veryStrongSingleReference
    || strongMultipleReferences;
}

export function rankRestaurants(restaurants, detectedText, visualScores = {}, manual = false) {
  const candidates = restaurants.map((restaurant) => {
    const textScore = scoreRestaurantText(detectedText, restaurant);
    const visualEvidence = normalizeVisualEvidence(visualScores[restaurant.id]);
    const visualScore = buildVisualScore(visualEvidence);
    let confidence;

    if (manual) {
      confidence = textScore;
    } else if (textScore > 0 && visualScore > 0) {
      confidence = textScore * 0.62 + visualScore * 0.38;
      if (textScore >= 0.62 && visualEvidence.best >= 0.68) confidence += 0.06;
    } else if (textScore > 0) {
      confidence = textScore * 0.92;
    } else {
      confidence = visualScore;
    }

    return {
      ...restaurant,
      textScore,
      visualScore,
      visualEvidence,
      confidence: clamp(confidence),
    };
  });

  const accepted = candidates.filter((candidate) => (
    manual ? candidate.textScore >= 0.5 : acceptsScannedMatch(candidate)
  ));

  if (!manual && import.meta.env?.DEV) {
    console.table(candidates.map((candidate) => ({
      restaurant: candidate.name,
      accepted: accepted.some((item) => item.id === candidate.id),
      confidence: candidate.confidence.toFixed(3),
      text: candidate.textScore.toFixed(3),
      visual: candidate.visualScore.toFixed(3),
      rawBest: candidate.visualEvidence.best.toFixed(3),
      rawSecond: candidate.visualEvidence.second.toFixed(3),
      references: candidate.visualEvidence.referenceCount,
    })));
  }

  return accepted
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, 8);
}

export function getTextCandidateIds(restaurants, text, ocrConfidence = 0) {
  const ranked = restaurants
    .map((restaurant) => ({ id: restaurant.id, score: scoreRestaurantText(text, restaurant) }))
    .sort((a, b) => b.score - a.score);

  const best = ranked[0]?.score || 0;
  const second = ranked[1]?.score || 0;

  // Never let weak or noisy OCR exclude the correct visual match.
  // Candidate narrowing is used only when OCR is clearly reliable and distinctive.
  if (ocrConfidence < 0.45 || best < 0.58 || best - second < 0.08) return null;

  const useful = ranked
    .filter((item) => item.score >= Math.max(0.42, best - 0.24))
    .slice(0, 20)
    .map((item) => item.id);
  return useful.length ? useful : null;
}
